import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, Clock, Menu, X, Star, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Offers', path: '/offers' },
    { label: 'Areas', path: '/areas' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top announcement strip — patriot red */}
      <AnimatePresence>
        {!isScrolled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-patriot text-white overflow-hidden"
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-8 text-[11px] font-semibold uppercase tracking-wider">
                <div className="hidden md:flex items-center gap-4">
                  <span className="flex items-center gap-1.5">
                    <Shield className="w-3 h-3" />
                    Licensed · Bonded · Insured
                  </span>
                  <span className="opacity-60">|</span>
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" />
                    <Star className="w-3 h-3 fill-current" />
                    <Star className="w-3 h-3 fill-current" />
                    <Star className="w-3 h-3 fill-current" />
                    <Star className="w-3 h-3 fill-current" />
                    <span className="ml-1">4.9 · 500+ Reviews</span>
                  </span>
                </div>
                <div className="flex items-center gap-4 ml-auto">
                  <a href="tel:2016570855" className="flex items-center gap-1.5 hover:text-cream transition-colors">
                    <Phone className="w-3 h-3" />
                    (201) 657-0855
                  </a>
                  <span className="hidden lg:flex items-center gap-1.5 opacity-80">
                    <Clock className="w-3 h-3" />
                    Mon–Sat
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main nav */}
      <div className={`transition-all duration-200 bg-navy ${isScrolled ? 'shadow-lg border-b border-lime/30' : ''}`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-200 ${isScrolled ? 'h-14' : 'h-16'}`}>
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-3">
              <span
                className="font-display font-bold text-white text-2xl tracking-wide leading-none"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.45)' }}
              >
                DeckPros<span className="text-lime">NJ</span>
              </span>
              <span className="hidden sm:flex items-center gap-2 pl-3 border-l border-white/20 leading-none">
                <span className="text-[8px] font-display font-bold uppercase tracking-[0.2em] text-white/50">
                  Powered<br/>by
                </span>
                <a
                  href="https://allcompleteconstruction.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="All Complete Construction"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center bg-white border-2 border-lime px-2.5 py-1.5 hover:shadow-hard transition-all"
                >
                  <img
                    src="https://allcompleteconstruction.com/wp-content/uploads/2018/12/all-complete-construction-logo-trans-301w.png"
                    alt="All Complete Construction"
                    className="h-8 w-auto"
                    loading="lazy"
                  />
                </a>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative text-[12px] font-display font-semibold uppercase tracking-[0.12em] transition-colors duration-200 px-3 py-2 ${
                    isActive(item.path)
                      ? 'text-patriot'
                      : 'text-white/85 hover:text-patriot'
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-3 right-3 h-[3px] bg-patriot" />
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a href="tel:2016570855" className="flex flex-col items-end leading-tight hover:text-patriot transition-colors group">
                <span className="text-[9px] text-cream/60 uppercase tracking-[0.2em] font-semibold">Call Now</span>
                <span className="font-display font-bold text-white text-sm group-hover:text-patriot transition-colors">
                  (201) 657-0855
                </span>
              </a>
              <Link to="/contact">
                <Button variant="cta" size="default" className="h-9 text-[11px] font-display font-bold uppercase tracking-[0.15em] px-4 rounded-none border-2 border-navy shadow-[4px_4px_0_#fff] hover:shadow-none">
                  Free Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-white p-2 hover:bg-white/10 transition-colors">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 bg-navy border-patriot/30 p-0">
                <SheetHeader className="text-left p-6 bg-navy-deep border-b-2 border-patriot">
                  <div className="flex items-center justify-between">
                    <SheetTitle className="text-white">
                      <div className="flex flex-col gap-1.5">
                        <span
                          className="font-display font-bold text-white text-2xl tracking-wide leading-none"
                          style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.45)' }}
                        >
                          DeckPros<span className="text-lime">NJ</span>
                        </span>
                        <span className="flex items-center gap-2 leading-none">
                          <span className="text-[8px] font-display font-bold uppercase tracking-[0.2em] text-white/50">
                            Powered by
                          </span>
                          <span className="inline-flex items-center bg-white border-2 border-lime px-2 py-1">
                            <img
                              src="https://allcompleteconstruction.com/wp-content/uploads/2018/12/all-complete-construction-logo-trans-301w.png"
                              alt="All Complete Construction"
                              className="h-6 w-auto"
                              loading="lazy"
                            />
                          </span>
                        </span>
                      </div>
                    </SheetTitle>
                    <button onClick={() => setIsMobileMenuOpen(false)} className="text-white hover:text-patriot transition-colors p-2">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </SheetHeader>

                <nav className="flex flex-col p-4">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.04 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block py-3 px-4 font-display font-semibold uppercase tracking-[0.12em] text-sm transition-all duration-150 border-l-4 ${
                          isActive(item.path)
                            ? 'bg-patriot/10 text-patriot border-patriot'
                            : 'text-white/80 hover:bg-white/5 hover:text-patriot border-transparent'
                        }`}
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  ))}

                  <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
                    <a href="tel:2016570855" className="flex items-center gap-3 py-3 px-4 text-white/80 hover:bg-white/5 transition-colors">
                      <Phone className="w-4 h-4 text-patriot" />
                      <span className="font-display font-bold">(201) 657-0855</span>
                    </a>
                    <a href="mailto:support@allcompleteconstruction.com" className="flex items-center gap-3 py-3 px-4 text-white/70 hover:bg-white/5 transition-colors">
                      <Mail className="w-4 h-4 text-patriot" />
                      <span className="text-xs">support@allcompleteconstruction.com</span>
                    </a>
                  </div>

                  <div className="mt-4">
                    <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="cta" size="lg" className="w-full font-display uppercase tracking-[0.15em] rounded-none">
                        Get Free Quote
                      </Button>
                    </Link>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        {/* Bottom accent stripe */}
        <div className="h-[2px] bg-gradient-to-r from-patriot via-cream to-patriot opacity-60" />
      </div>
    </header>
  );
};
